import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/Layout/MainLayout';
import { ArrowLeft, Eye, EyeOff, Shield, Smartphone, Key } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const Security: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [security, setSecurity] = useState({
    twoFactorAuth: false,
    loginNotifications: true,
    deviceRemembering: true
  });
  const [sessions, setSessions] = useState<any[]>([]);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', user?.id)
        .order('last_active', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswords(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSecurityToggle = async (key: string) => {
    setSecurity(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev]
    }));

    // Log security changes
    try {
      await supabase
        .from('security_audit_log')
        .insert({
          user_id: user?.id,
          event_type: 'security_setting_changed',
          event_data: { setting: key, enabled: !security[key as keyof typeof security] },
          ip_address: null,
          user_agent: navigator.userAgent
        });
    } catch (error) {
      console.error('Error logging security change:', error);
    }

    toast({
      title: t('settings.securitySettingUpdated'),
      description: !security[key as keyof typeof security] ? t('common.enabled') : t('common.disabled'),
    });
  };

  const handlePasswordUpdate = async () => {
    // Demo user check
    if (user?.id === '1') {
      toast({
        title: t('settings.demoAccount'),
        description: t('settings.demoPasswordUnavailable'),
        variant: "destructive",
      });
      return;
    }

    if (passwords.new !== passwords.confirm) {
      toast({
        title: t('settings.passwordMismatch'),
        description: t('settings.passwordsDoNotMatch'),
        variant: "destructive",
      });
      return;
    }

    if (passwords.new.length < 6) {
      toast({
        title: t('settings.passwordTooShort'),
        description: t('settings.passwordMinSix'),
        variant: "destructive",
      });
      return;
    }

    setIsUpdating(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwords.new
      });

      if (error) throw error;

      // Log password change for authenticated users
      if (user?.id) {
        try {
          await supabase
            .from('security_audit_log')
            .insert({
              user_id: user.id,
              event_type: 'password_changed',
              event_data: { timestamp: new Date().toISOString() },
              ip_address: null,
              user_agent: navigator.userAgent
            });
        } catch (auditError) {
          console.error('Failed to log password change:', auditError);
        }
      }

      toast({
        title: t('settings.passwordUpdated'),
        description: t('settings.passwordUpdatedDesc'),
      });

      setPasswords({
        current: '',
        new: '',
        confirm: ''
      });
    } catch (error: any) {
      toast({
        title: t('settings.passwordUpdateFailed'),
        description: error.message || t('settings.passwordUpdateFailedDesc'),
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const signOutSession = async (sessionId: string) => {
    try {
      await supabase
        .from('user_sessions')
        .delete()
        .eq('id', sessionId);

      setSessions(sessions.filter(s => s.id !== sessionId));
      
      toast({
        title: t('settings.sessionEnded'),
        description: t('settings.sessionEndedDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.errorTitle'),
        description: t('settings.failedSignOutDevice'),
        variant: "destructive",
      });
    }
  };

  const signOutAllOtherSessions = async () => {
    try {
      await supabase
        .from('user_sessions')
        .delete()
        .eq('user_id', user?.id)
        .eq('is_current', false);

      setSessions(sessions.filter(s => s.is_current));
      
      toast({
        title: t('settings.allSessionsEnded'),
        description: t('settings.allSessionsEndedDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.errorTitle'),
        description: t('settings.failedSignOutOthers'),
        variant: "destructive",
      });
    }
  };

  return (
    <MainLayout title={t('settings.security')}>
      <div className="p-4 space-y-6">
        <button onClick={() => navigate('/settings')} className="flex items-center space-x-2 text-blue-500 hover:text-blue-600">
          <ArrowLeft size={20} />
          <span>{t('settings.backToSettings')}</span>
        </button>

        {/* Change Password */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.changePassword')}</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.currentPasswordLabel')}</label>
              <div className="relative">
                <input 
                  type={showCurrentPassword ? 'text' : 'password'} 
                  value={passwords.current} 
                  onChange={e => handlePasswordChange('current', e.target.value)} 
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black" 
                />
                <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.newPasswordLabel')}</label>
              <div className="relative">
                <input 
                  type={showNewPassword ? 'text' : 'password'} 
                  value={passwords.new} 
                  onChange={e => handlePasswordChange('new', e.target.value)} 
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black" 
                />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.confirmNewPassword')}</label>
              <div className="relative">
                <input 
                  type={showConfirmPassword ? 'text' : 'password'} 
                  value={passwords.confirm} 
                  onChange={e => handlePasswordChange('confirm', e.target.value)} 
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black" 
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button 
              onClick={handlePasswordUpdate} 
              disabled={isUpdating}
              className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? t('settings.updating') : t('settings.updatePassword')}
            </button>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.securitySettings')}</h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Smartphone size={20} className="text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{t('settings.twoFactorAuth')}</p>
                  <p className="text-sm text-gray-500">{t('settings.twoFactorDesc')}</p>
                </div>
              </div>
              <button onClick={() => handleSecurityToggle('twoFactorAuth')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${security.twoFactorAuth ? 'bg-blue-500' : 'bg-gray-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${security.twoFactorAuth ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Shield size={20} className="text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{t('settings.loginNotifications')}</p>
                  <p className="text-sm text-gray-500">{t('settings.loginNotificationsDesc')}</p>
                </div>
              </div>
              <button onClick={() => handleSecurityToggle('loginNotifications')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${security.loginNotifications ? 'bg-blue-500' : 'bg-gray-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${security.loginNotifications ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Key size={20} className="text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{t('settings.rememberDevices')}</p>
                  <p className="text-sm text-gray-500">{t('settings.rememberDevicesDesc')}</p>
                </div>
              </div>
              <button onClick={() => handleSecurityToggle('deviceRemembering')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${security.deviceRemembering ? 'bg-blue-500' : 'bg-gray-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${security.deviceRemembering ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Device Management */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('settings.deviceManagement')}</h3>
            <button
              onClick={signOutAllOtherSessions}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              {t('settings.signOutAllOthers')}
            </button>
          </div>
          
          <div className="space-y-3">
            {sessions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">{t('settings.noActiveSessions')}</p>
            ) : (
              sessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{session.device_name || t('settings.unknownDevice')}</p>
                    <p className="text-sm text-gray-500">
                      {session.device_type} • {session.is_current ? t('settings.activeNow') : new Date(session.last_active).toLocaleDateString()}
                    </p>
                    {session.ip_address && (
                      <p className="text-sm text-gray-500">{t('settings.ipLabel', { ip: session.ip_address })}</p>
                    )}
                  </div>
                  {session.is_current ? (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      {t('settings.current')}
                    </span>
                  ) : (
                    <button
                      onClick={() => signOutSession(session.id)}
                      className="px-3 py-1 text-xs text-red-600 border border-red-300 rounded hover:bg-red-50"
                    >
                      {t('settings.signOut')}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Security;